/**
 * Stripe Connect self-enrollment — the vendor portal's own WRITE path.
 * Direct mirror of OwnerPayoutsPage.tsx; see that file's header for the
 * five-state branching shape and why the Account Link is always minted
 * fresh rather than cached, and why the return URL is never treated as a
 * success signal.
 */

import { useState } from 'react';
import { useOne } from '@refinedev/core';
import type { VendorConnectStatus } from 'shared';
import { useSession } from '../../../lib/session.js';
import { Bento, BentoCell } from '../../../components/Bento.js';
import { api, ApiError } from '../../../lib/api.js';

interface VendorRecord {
  connect: {
    status: VendorConnectStatus;
    requirementsDue: string[];
    enrolledAt: string | null;
  };
}

export function VendorPayoutsPage() {
  const { state } = useSession();
  const vendorId = state.status === 'signed-in' ? state.me.actor.vendorId : null;

  const { data, isLoading } = useOne<VendorRecord>({
    resource: 'vendors',
    id: vendorId ?? '',
    queryOptions: { enabled: Boolean(vendorId) },
  });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function startOrContinueEnrollment() {
    setBusy(true);
    setError(null);
    try {
      const { accountLinkUrl } = await api<{ accountLinkUrl: string }>('/vendor-connect/enroll', {
        method: 'POST',
      });
      window.location.href = accountLinkUrl;
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Could not start enrollment. Try again in a moment.',
      );
      setBusy(false);
    }
  }

  if (state.status !== 'signed-in') return null;

  const connect = data?.data.connect;

  return (
    <section>
      <h1>Payouts</h1>
      <p className="muted">
        Set up direct deposit for your completed work order payouts. This is a short, secure setup
        process hosted by Stripe, our payments partner — we never see your bank account or routing
        number.
      </p>
      {error ? <p className="alert alert--error">{error}</p> : null}

      <Bento>
        {isLoading || !connect ? (
          <BentoCell span="full">
            <p className="bento__hint">Loading…</p>
          </BentoCell>
        ) : connect.status === 'NOT_INVITED' || connect.status === 'INVITED' ? (
          <BentoCell span="full" title="Set up direct deposit">
            <p className="bento__hint">
              You haven't set up direct deposit yet. It only takes a few minutes.
            </p>
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() => void startOrContinueEnrollment()}
            >
              {busy ? 'Starting…' : 'Enroll'}
            </button>
          </BentoCell>
        ) : connect.status === 'ONBOARDING_STARTED' || connect.status === 'PENDING_VERIFICATION' ? (
          <BentoCell span="full" title="Finish setting up direct deposit">
            <p className="bento__hint">
              Your setup is in progress. Pick up where you left off to finish verification.
            </p>
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() => void startOrContinueEnrollment()}
            >
              {busy ? 'Loading…' : 'Continue'}
            </button>
          </BentoCell>
        ) : connect.status === 'REQUIREMENTS_DUE' ? (
          <BentoCell span="full" tone="attention" title="Action needed on your payout account">
            <p className="bento__hint">
              Stripe needs updated information before your next payout can go through.
            </p>
            {connect.requirementsDue.length > 0 ? (
              <ul className="bento__sub">
                {connect.requirementsDue.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() => void startOrContinueEnrollment()}
            >
              {busy ? 'Loading…' : 'Fix now'}
            </button>
          </BentoCell>
        ) : (
          <BentoCell span="full" title="Direct deposit is set up">
            <p className="bento__hint">
              Your account is enrolled
              {connect.enrolledAt ? ` since ${connect.enrolledAt.slice(0, 10)}` : ''}. Payouts will
              be transferred directly to your bank account.
            </p>
          </BentoCell>
        )}
      </Bento>
    </section>
  );
}
